import { useState } from 'react'
import {
  Badge,
  Button,
  Dialog,
  Flex,
  For,
  Heading,
  HStack,
  List,
  Portal,
  Span,
  Spinner,
  Stack,
  Text,
  VStack,
  Box,
} from '@chakra-ui/react'
import { LuCheck, LuX } from 'react-icons/lu'
import CustomDialogBackdrop from '@/components/CustomDialogBackdrop'
import { formatCurrency } from '@/utils/currency'
import { formatMembersLabel, formatStorageLabel } from '../utils'
import { usePlans } from '@/hooks/subscriptions/usePlans'
import type { Plan } from '@/services/subscriptions'
import CheckoutModal from './CheckoutModal'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentPlanId?: number
}

const PlanCard = ({ plan, isCurrent }: { plan: Plan; isCurrent: boolean }) => {
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const features = [
    formatMembersLabel(plan.max_members),
    formatStorageLabel(plan.max_storage_mb),
    ...(plan.can_view_financial ? ['Dados financeiros'] : []),
  ]

  return (
    <Stack
      p="8"
      borderWidth="1px"
      borderColor={isCurrent ? 'primary.500' : undefined}
      gap="7"
      bg="bg.panel"
      pos="relative"
      flex={{ base: '1 1 100%', md: '0 0 264px' }}
      w={{ base: 'full', md: '264px' }}
      maxW={{ base: 'full', md: '264px' }}
      rounded="l3"
      align="stretch"
      justify="space-between"
    >
      <Stack gap="7">
        <HStack fontWeight="medium" color="colorPalette.fg" justify="space-between">
          {plan.name}
          {isCurrent && <Badge colorPalette="primary" variant="solid" size="sm">Plano atual</Badge>}
        </HStack>

        <Flex gap="1" align="flex-end">
          <Span textStyle="2xl" lineHeight="1" fontWeight="semibold">
            {formatCurrency(plan.price)}
          </Span>
          <Span textStyle="sm" color="fg.muted" alignSelf="center" ms="1">
            / {plan.billing_cycle}
          </Span>
        </Flex>

        {plan.description && (
          <Text textStyle="sm" color="fg.muted">
            {plan.description}
          </Text>
        )}

        <List.Root variant="plain" align="center" textStyle="sm" gap="3">
          <For each={features}>
            {(item) => (
              <List.Item alignItems="center" key={item}>
                <List.Indicator asChild color="colorPalette.fg">
                  <LuCheck />
                </List.Indicator>
                {item}
              </List.Item>
            )}
          </For>
        </List.Root>
      </Stack>

      {!isCurrent && plan.price > 0 && (
        <>
          <Button colorPalette="primary" onClick={() => setCheckoutOpen(true)}>
            Assinar plano
          </Button>
          <CheckoutModal
            open={checkoutOpen}
            onOpenChange={setCheckoutOpen}
            planId={plan.id}
            planName={plan.name}
          />
        </>
      )}
    </Stack>
  )
}

const PlansModal = ({ open, onOpenChange, currentPlanId }: Props) => {
  const { data: plans, isLoading } = usePlans()

  return (
    <Dialog.Root
      placement="center"
      open={open}
      onOpenChange={({ open }) => onOpenChange(open)}
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxW={{ base: 'full', md: '6xl' }}
            w={{ base: 'full', md: '95vw' }}
            h={{ base: '100dvh', md: 'auto' }}
            maxH={{ base: '100dvh', md: 'auto' }}
            m={{ base: 0, md: 6 }}
            borderRadius={{ base: 0, md: 'lg' }}
            display="flex"
            flexDirection="column"
          >
            <Dialog.Header flexShrink={0}>
              <Dialog.Title>
                <VStack gap="1" align="flex-start">
                  <Heading size="lg">Planos disponíveis</Heading>
                  <Text fontSize="sm" fontWeight="normal" color="fg.muted">
                    Escolha o plano ideal para sua agenda
                  </Text>
                </VStack>
              </Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <Button variant="ghost" size="sm" aria-label="Fechar">
                  <LuX />
                </Button>
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body pb="8" overflowY="auto" flex="1">
              {isLoading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <Spinner size="lg" color="primary.500" />
                </Box>
              ) : !plans?.length ? (
                <Text color="fg.muted" fontSize="sm">
                  Nenhum plano disponível no momento.
                </Text>
              ) : (
                <Stack direction={{ base: 'column', md: 'row' }} gap="4" align="stretch" flexWrap="wrap" justify="center">
                  <For each={plans}>
                    {(plan) => (
                      <PlanCard key={plan.id} plan={plan} isCurrent={plan.id === currentPlanId} />
                    )}
                  </For>
                </Stack>
              )}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default PlansModal
